// Quest / Puzzle-Dependency-Graph analyzer — the LOGICAL completability layer (research
// REBUILD §8). Pure (no deps) so it runs in CI. It reasons over the room graph + GLOBAL
// state (the persistent quest layer): which rooms are reachable, which global flags can be
// achieved, whether the win is reachable, and what's orphaned or soft-lockable.
//
// Modeling choices (deliberate, documented):
//  - It analyzes GLOBAL state only. Room-LOCAL puzzles are assumed solvable within a visit
//    (a room-scope gate is treated as satisfiable when its room is reachable) — their
//    mechanical solvability is verified separately by the per-room nav/state oracles. This
//    is the research's "two complementary layers": logical (here) + mechanical (oracles).
//  - Global flags are modeled MONOTONICALLY (set/toggle can reach `true`), the standard
//    puzzle-dependency assumption. Non-monotonic risk (a quest flag that can be turned
//    OFF) is reported separately as a soft-lock risk.
import type { Cond, Room, StateVal } from "../core/schema";
import { holds, flameOf, FLAME, type RoomState } from "./roomState";

/** Win when ALL listed conditions hold in global state. */
export interface QuestSpec {
  win: Cond[];
  /** global flags the game starts with (start mid-saga: "the flame is already lit") */
  initialGlobal?: RoomState;
}

export interface QuestReport {
  ok: boolean; // no hard errors (win is reachable)
  winReachable: boolean;
  reachableRooms: string[];
  unreachableRooms: string[];
  achievable: RoomState; // global flags that can be achieved from the start
  orphanActions: string[]; // global-effecting interactions that can never fire
  softlockRisks: string[]; // quest-relevant global flags that can be turned off
  errors: string[];
  warnings: string[];
}

// A gate is open per the shared `holds` rule: room-scope keys read the room's LOCAL
// achievability (see localReach), global-scope keys + minFlame read the global flags. Reusing
// holds keeps the analyzer in lockstep with the runtime (and gets minFlame/optional-key free).
const gateHolds = holds;

/** Monotonic fixpoint over a room's LOCAL state: which room flags are achievable in a
 * single visit (firing room-scope-gated hotspots) given the current global flags. A
 * circular local dependency (lever A needs gate B, lever B needs gate A) reaches no new
 * flags — so the gates it would open are correctly reported as never-satisfiable. */
function localReach(room: Room, global: RoomState): { state: RoomState; fired: Set<string> } {
  const rs: RoomState = { ...room.state };
  const fired = new Set<string>(); // hotspot ids whose gate opened during the local solve
  let changed = true;
  while (changed) {
    changed = false;
    for (const h of room.hotspots) {
      // fire each hotspot at most once; record it so the caller can credit its global/flame
      // effects (we must report the firing, not just the post-fire state, because a hotspot's
      // own gate may consume the flag it sets — e.g. a hearth gated by !lit that sets lit).
      if (!h.effect || fired.has(h.id) || !gateHolds(h.gatedBy, rs, global)) continue;
      fired.add(h.id);
      changed = true;
      if (h.effect.set) for (const [k, v] of Object.entries(h.effect.set)) rs[k] = v as StateVal;
      if (h.effect.toggle) for (const k of h.effect.toggle) rs[k] = !rs[k];
    }
  }
  return { state: rs, fired };
}

export function analyzeQuest(rooms: Record<string, Room>, startRoomId: string, quest: QuestSpec): QuestReport {
  const errors: string[] = [];
  const warnings: string[] = [];
  const reachable = new Set<string>();
  const flags: RoomState = { ...(quest.initialGlobal ?? {}) }; // start from any pre-set saga flags

  if (!rooms[startRoomId]) {
    errors.push(`start room '${startRoomId}' not found`);
    return { ok: false, winReachable: false, reachableRooms: [], unreachableRooms: Object.keys(rooms), achievable: {}, orphanActions: [], softlockRisks: [], errors, warnings };
  }
  reachable.add(startRoomId);

  // Monotonic forward fixpoint: keep firing reachable global actions (each at most ONCE, via
  // `fired`, so flame sums correctly and the loop terminates) + traversing open exits until
  // nothing new is reachable. minFlame gates re-open as the flame grows across iterations.
  const fired = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const id of [...reachable]) {
      const room = rooms[id]!;
      const { state: local, fired: localFired } = localReach(room, flags); // what this room can do now
      for (const h of room.hotspots) {
        const fid = `${id}.${h.id}`;
        const e = h.effect;
        if (!e || fired.has(fid) || !localFired.has(h.id)) continue; // only credit hotspots that fire
        if (!e.setGlobal && !e.toggleGlobal && !e.flame) continue; // no global effect to credit
        if (e.setGlobal) for (const [k, v] of Object.entries(e.setGlobal)) flags[k] = v as StateVal;
        if (e.toggleGlobal) for (const k of e.toggleGlobal) flags[k] = true; // can reach true
        if (e.flame) flags[FLAME] = flameOf(flags) + e.flame; // kindling grows the carried flame (once)
        fired.add(fid);
        changed = true;
      }
      for (const ex of room.exits) {
        if (gateHolds(ex.gatedBy, local, flags) && rooms[ex.target] && !reachable.has(ex.target)) {
          reachable.add(ex.target);
          changed = true;
        }
      }
    }
  }

  const winReachable = quest.win.every((c) => gateHolds(c, {}, flags));
  if (!winReachable) errors.push("win condition is not reachable from the start");

  const unreachableRooms = Object.keys(rooms).filter((id) => !reachable.has(id));
  unreachableRooms.forEach((id) => warnings.push(`room '${id}' is unreachable from the start`));

  // orphan actions: global-effecting interactions that can never fire (unreachable room,
  // or a global gate that never opens)
  const orphanActions: string[] = [];
  for (const [id, room] of Object.entries(rooms)) {
    const { fired: localFired } = localReach(room, flags); // which hotspots can ever fire here
    for (const h of room.hotspots) {
      if (!h.effect?.setGlobal && !h.effect?.toggleGlobal && !h.effect?.flame) continue;
      const unreachableRoom = !reachable.has(id);
      const neverFires = !localFired.has(h.id); // gate (room/global/flame) never opens
      if (unreachableRoom || neverFires) orphanActions.push(`${id}.${h.id}`);
    }
  }
  orphanActions.forEach((a) => warnings.push(`global action '${a}' can never fire`));

  // soft-lock risk: any quest-relevant global flag (used by the win or any global gate)
  // that some action can turn OFF (toggle, or set false) — breaks the monotonic assumption.
  const questKeys = new Set<string>();
  const addKey = (c: { scope?: string; key?: string } | undefined) => {
    if (c && c.scope === "global" && c.key !== undefined) questKeys.add(c.key);
  };
  quest.win.forEach(addKey);
  for (const room of Object.values(rooms)) {
    for (const a of room.gatedAreas) addKey(a.gatedBy);
    for (const h of room.hotspots) addKey(h.gatedBy);
    for (const ex of room.exits) addKey(ex.gatedBy);
  }
  const softlockRisks: string[] = [];
  for (const room of Object.values(rooms)) {
    for (const h of room.hotspots) {
      if (!h.effect) continue;
      const offable = [
        ...(h.effect.toggleGlobal ?? []),
        ...Object.entries(h.effect.setGlobal ?? {}).filter(([, v]) => !v).map(([k]) => k),
      ];
      for (const k of offable) if (questKeys.has(k) && !softlockRisks.includes(k)) softlockRisks.push(k);
    }
  }
  softlockRisks.forEach((k) => warnings.push(`quest-relevant global flag '${k}' can be turned off (soft-lock risk)`));

  return {
    ok: errors.length === 0,
    winReachable,
    reachableRooms: [...reachable],
    unreachableRooms,
    achievable: flags,
    orphanActions,
    softlockRisks,
    errors,
    warnings,
  };
}
